// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ArtworkRegionCitationLink.test.tsx
import { type ArtworkRegionCitation } from "@mg/schemas/src/commons/ticket";
import { FileMagnifyingGlass } from "@phosphor-icons/react";
import { Button, Dialog, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import {
  usePackArtworkFileQuery,
  usePackMarkCropQuery,
} from "../../../../utils/queries/askPuntt";
import { PdfCitationPreview } from "../../../settings/routes/knowledge-database/components/PdfCitationPreview";

const PACK_CITATION_PREFIX = "pack-citation";

/** The chip chrome every pack citation chip shares. */
export const PACK_CHIP_CLASS =
  "inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 px-2.5 py-1 text-xs font-medium text-puntt-neutral-gray-12 transition-colors hover:bg-puntt-neutral-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9";

/** Opens the cited artwork PDF at the cited page with the box highlighted.
 * Its own component so the download hook stays unconditional and only runs
 * once the dialog is open. */
function PackArtworkPin({ citation }: { citation: ArtworkRegionCitation }) {
  const { t } = useTranslation("ui");
  const { data, isLoading } = usePackArtworkFileQuery(citation.sourceId);
  if (isLoading) {
    return (
      <Text
        size="1"
        className="text-puntt-neutral-gray-11"
        data-testid={`${PACK_CITATION_PREFIX}-pin-loading`}
      >
        {t("packCitation.loading")}
      </Text>
    );
  }
  // A failed download or a non-PDF keeps the text-only citation instead of a
  // broken viewer.
  if (!data || data.mimeType !== "application/pdf") return null;
  const { page, x, y, w, h } = citation.region;
  return (
    <PdfCitationPreview
      data={data.bytes}
      pageNumber={page}
      bbox={{ x, y, w, h }}
      testIdPrefix={PACK_CITATION_PREFIX}
    />
  );
}

/** The mark a logo row was read from, cut from its artwork. Its own component
 * so the link is only asked for once the dialog is open. */
function PackMarkCrop({
  cropKey,
  alt,
  index,
}: {
  cropKey: string;
  alt: string;
  index: number;
}) {
  const { data } = usePackMarkCropQuery(cropKey);
  if (!data?.url) return null;
  return (
    <img
      src={data.url}
      alt={alt}
      data-testid={`pack-citation-crop-${index}`}
      className="max-h-24 w-auto max-w-full self-start rounded-sm border border-puntt-neutral-gray-6 object-contain"
    />
  );
}

/** One classified line printed at the cited spot: its value, status and
 * confidence, and its wording with the English (or, for a logo, the mark). */
function PackCitationLine({
  citation,
  index,
}: {
  citation: ArtworkRegionCitation;
  index: number;
}) {
  const { t } = useTranslation("ui");
  const isLogo = citation.listKind === "logo";
  const status = t(
    citation.status === "exact"
      ? "packCitation.statusExact"
      : "packCitation.statusPossible",
  );
  const kindLabel = t(
    citation.listKind === "territory"
      ? "packCitation.kindTerritory"
      : citation.listKind === "claim_type"
        ? "packCitation.kindClaimType"
        : "packCitation.kindLogo",
  );
  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`pack-citation-line-${index}`}
    >
      <Text
        size="2"
        weight="medium"
        data-testid={`pack-citation-label-${index}`}
      >
        {kindLabel}: {citation.label}
      </Text>
      <div className="flex flex-col gap-1">
        <Text size="2" data-testid={`pack-citation-confidence-${index}`}>
          {t("packCitation.confidence", {
            status,
            percent: Math.round(citation.confidence * 100),
          })}
        </Text>
        {citation.status === "possible" && citation.reason ? (
          <Text
            size="1"
            className="text-puntt-neutral-gray-11"
            data-testid={`pack-citation-reason-${index}`}
          >
            {t("packCitation.reason", { reason: citation.reason })}
          </Text>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <Text size="1" className="text-puntt-neutral-gray-11">
          {isLogo
            ? t("packCitation.markName")
            : t("packCitation.printedWording")}
        </Text>
        {citation.quote ? (
          <Text size="2" data-testid={`pack-citation-quote-${index}`}>
            {citation.quote}
          </Text>
        ) : null}
        {!isLogo && citation.textEn ? (
          <Text
            size="2"
            className="text-puntt-neutral-gray-11"
            data-testid={`pack-citation-text-en-${index}`}
          >
            {t("packCitation.englishText")}: {citation.textEn}
          </Text>
        ) : null}
        {isLogo && citation.cropKey ? (
          <PackMarkCrop
            cropKey={citation.cropKey}
            alt={citation.quote || citation.label}
            index={index}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * The dialog for one cited spot on one artwork: every classified line printed
 * there, and the PDF opened once at its page with the box highlighted. Rendered
 * inside a `Dialog.Root`, so a chip can be its trigger or a list entry can
 * open it.
 */
export function PackCitationDialogContent({
  citations,
}: {
  citations: ArtworkRegionCitation[];
}) {
  const { t } = useTranslation("ui");
  const first = citations[0];
  if (!first) return null;
  const title = first.product?.trim() || first.fileName;
  const meta = [
    first.ean ? t("packCitation.ean", { ean: first.ean }) : "",
    first.fileName,
    t("packCitation.page", { page: first.region.page }),
  ]
    .filter(Boolean)
    .join(" - ");
  return (
    <Dialog.Content
      data-testid="pack-citation-dialog"
      className="flex max-h-[90vh] flex-col gap-3"
      // why `overflow: clip` and not a scrolling dialog: PdfCitationPreview
      // centres the box with scrollIntoView, which scrolls every scrollable
      // ancestor, and would carry the status and wording out of view. A clip
      // box is not a scroll container. Inline because eslint rewrites the
      // utility class into `text-clip`, a different property.
      style={{ overflow: "clip" }}
      maxWidth="1100px"
    >
      <Dialog.Title mb="0">{title}</Dialog.Title>
      <Dialog.Description size="1" className="text-puntt-neutral-gray-11">
        {meta}
      </Dialog.Description>
      <div className="flex min-h-0 flex-col gap-4 md:flex-row">
        {/* why a smaller cap below md: the columns stack there inside a clipped dialog, and a 70vh list left the
            artwork a sliver no one could scroll to */}
        <div
          data-testid="pack-citation-lines"
          className="flex max-h-[35vh] shrink-0 flex-col gap-4 overflow-y-auto md:max-h-[70vh] md:w-72"
        >
          {citations.map((citation, index) => (
            <PackCitationLine
              // Stryker disable next-line LogicalOperator: the key also carries the map index, which is unique among the lines, so every replacement still yields a unique key; PackCitationLine holds no state of its own, so the literal key text is unobservable.
              key={`${citation.label}-${citation.quote ?? ""}-${index}`}
              citation={citation}
              index={index}
            />
          ))}
          <Dialog.Close>
            <Button
              type="button"
              variant="soft"
              color="gray"
              className="self-start"
              data-testid="pack-citation-close"
            >
              {t("packCitation.close")}
            </Button>
          </Dialog.Close>
        </div>
        <div
          data-testid="pack-citation-artwork"
          className="min-h-0 min-w-0 flex-1"
        >
          <PackArtworkPin citation={first} />
        </div>
      </div>
    </Dialog.Content>
  );
}

/** One pack-artwork citation as its own chip: the review comment panel, where
 * a comment cites a single spot. The chat footer groups them per pack instead
 * (`PackCitationChip`). */
export function ArtworkRegionCitationLink({
  citation,
  chipTestId,
}: {
  citation: ArtworkRegionCitation;
  chipTestId: string;
}) {
  const { t } = useTranslation("ui");
  const title = citation.product?.trim() || citation.fileName;
  const status = t(
    citation.status === "exact"
      ? "packCitation.statusExact"
      : "packCitation.statusPossible",
  );
  const page = citation.region.page;
  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <button
          type="button"
          data-testid={chipTestId}
          aria-label={t("packCitation.chipAriaLabel", {
            label: title,
            page,
            status,
          })}
          className={PACK_CHIP_CLASS}
        >
          <FileMagnifyingGlass
            size={13}
            className="text-puntt-accent-11"
            aria-hidden="true"
          />
          <span>
            {t("packCitation.chipText", { label: title, page, status })}
          </span>
        </button>
      </Dialog.Trigger>
      <PackCitationDialogContent citations={[citation]} />
    </Dialog.Root>
  );
}
